<template>
  <base-hm-input :id="id"
                 :class="[
                   $attrs['class'],
                   { 'has-inline-icon-left': hasSlot('icon-inline-left') },
                   { 'has-inline-icon-right': hasSlot('icon-inline-right') },
                   { 'component__hm-input-select--vague': vague === true },
                 ]"
                 :label="label"
                 :help="help"
                 :has-focus="hasFocus"
                 :is-empty="isValueEmpty"
                 :name="name"
                 :cols="cols"
                 :hidden="hidden"
                 :style="iconCountStyleVariables"
                 :required="required"
                 :has-warning="hasWarning"
                 :disabled="disabledCombined">
    <select v-if="!isNil(EnumName)"
            ref="select"
            :id="`${id}-input`"
            v-model.lazy="internalValue"
            :data-vv-as="label"
            :data-vv-name="name"
            :disabled="disabledCombined"
            :class="['hm-input__select', { 'privacy--blur': privacyBlur}]"
            type="text"
            @focus="onFocus"
            @blur="onBlur">
      <option v-if="noSelect" :value="null"></option>
      <option v-for="item in enums[EnumName]" :value="item" :disabled="!isNil(disabledOptions) && disabledOptions.includes(item)" v-html="getOptionLabel(item)" />
      <slot />
    </select>
    <select v-else
            ref="select"
            :id="`${id}-input`"
            v-model.lazy="internalValue"
            :data-vv-as="label"
            :data-vv-name="name"
            :disabled="disabledCombined"
            :class="['hm-input__select', { 'privacy--blur': privacyBlur}]"
            type="text"
            @focus="onFocus"
            @blur="onBlur">
      <slot />
    </select>
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper hm-input__icon__wrapper--left">
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-left"></slot>
    </div>
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper hm-input__icon__wrapper--right">
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-right"></slot>
    </div>
  </base-hm-input>
</template>

<script>

import { isNil, trim, isArray, isEmpty } from 'lodash';
import HmInputMixin from '@/mixins/HmInputMixin';

export default {
  name: 'HmInputSelect',

  mixins: [HmInputMixin],

  props: {
    EnumName: {
      type: String,
      default: '',
    },
    noSelect: {
      type: Boolean,
      default: false,
    },
    noSelectLabel: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    vague: {
      type: Boolean,
      default: false,
    },
    disabledOptions: {
      type: Array,
      default: () => [],
    },
  },

  data: function () {
    return {
      hover: false,
      displayLabel: null,
    };
  },

  computed: {
    isValueEmpty() {
      if (this.noSelectLabel) return false;
      else if (!isEmpty(this.displayLabel)) return false;
      return isNil(this.value) || trim(this.value).length === 0;
    },
    iconCountLeft: function () {
      const slot = this.getSlot('icon-inline-left');

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    iconCountRight: function () {
      const slot = this.getSlot('icon-inline-right');

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    iconCountStyleVariables: function () {
      return {
        '--icons-left': this.iconCountLeft,
        '--icons-right': this.iconCountRight,
      };
    },
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
  },

  watch: {
    internalValue: function () {
      this.updateDisplayLabel();
    },
  },
  methods: {
    getOptionLabel(item) {
      const key = `${this.EnumName}.${item}`;
      const t = this.$t(`${this.EnumName}.${item}`);

      return t === key ? item : t;
    },
    getDisplayLabel: function () {
      if (!isNil(this.$refs) && !isNil(this.$refs.select)) {
        const select = this.$refs.select;
        if (!isNil(select) && !isEmpty(select.options)) {
          const options = select.options;
          if (!isEmpty(select.options) && !isNil(options.selectedIndex)) {
            const option = select.options[options.selectedIndex];
            if (!isNil(option) && !isEmpty(option.label)) {
              return option.label;
            }
          }
        }
      }
      return null;
    },
    updateDisplayLabel: function () {
      this.displayLabel = this.getDisplayLabel();
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
   @import '~styles/components/lib/HmInput';

  select.hm-input__select {
    background-image: url(~svg/collapse.svg);
    background-repeat: no-repeat;
    background-size: 3.2rem 3.2rem;
    background-position: 100% 0;
    background-position: calc(100% - 3.1rem * var(--icons-right)) 0;
    appearance: none;
    -webkit-appearance: none;
    padding-right: 3.2rem;

    &:active {
      background-image: url(~svg/collapse_active.svg);
    }

    &::-ms-expand {
      display: none;
    }
  }

   .component__hm-input-select.text-right select, .component__hm-input-select.text-right option {
     text-align: right;
     padding-right: calc(3.2rem + 2.1rem)
   }
</style>

<style lang="scss">
  @import '~styles/import';

  .hm-input__select.searching {
    .selected-tag {
      opacity: 0 !important;
      pointer-events: none !important;
    }
  }

  .component__hm-input-select {
    &--vague {
      select:not([disabled]):not(.disabled) {
        background-color: rgba(255, 200, 0, .12)!important;
      }
    }

    .inline-button {
      @include icon-color($middlegray);
      @include icon-hover($text);
      &:hover {
        cursor: pointer;
      }

      &[disabled], &.disabled {
        cursor: not-allowed;
      }
    }
  }

  .component__hm-input-select .hm-input__icon__wrapper--right:after {
    content: ' ';
    position: absolute;
    top: 0;
    bottom: 0;
    right: calc(var(--icons-right) * 3.2rem);
    width: 3.1rem;
    background: transparent;
    pointer-events: none;
    border-left: 1px solid $middlegray;
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmInputSelect.vue